import React, { useEffect, useState } from "react";
import Select from "react-select";

import { addHorseAlerts, renderNoHorsesFoundMessage } from "../../api/helpers";
import { horseListService } from "../../api/horseList.service";
import { horseService } from "../../api/horses.service";
import useSourAlert from "../../hooks/useSourAlert";
import AddHorseModal from "../AddHorseModal";
import SourAlert from "../elements/SourAlert";
import FamilyModal from "./FamilyModal";
import ListsModal from "./ListsModal";

function useHorseOptions(inputValue) {
	const [options, setOptions] = useState([]);
	const [message, setMessage] = useState("Type at least 3 characters");

	useEffect(() => {
		const keyword = inputValue.trim();
		if (keyword.length < 3) {
			setOptions([]);
			setMessage("Type at least 3 characters");
			return undefined;
		}

		let cancelled = false;
		setMessage("Searching…");
		const timer = window.setTimeout(() => {
			horseService
				.find({ keyword })
				.then((response) => {
					if (cancelled) return;
					const nextOptions = (response || []).map((horse) => ({
						value: horse.id,
						label: horse.display_name || horse.name,
					}));
					setOptions(nextOptions);
					setMessage(nextOptions.length ? "Type at least 3 characters" : "No horses found");
				})
				.catch(() => {
					if (!cancelled) {
						setOptions([]);
						setMessage("Search failed — try again");
					}
				});
		}, 500);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [inputValue]);

	return { options, message };
}

export function getMultiReportGuidance({ name, sireCount, damCount, combinations }) {
	if (!String(name || "").trim()) return "Add a report name in step 1.";
	if (!sireCount) return "Add at least one sire.";
	if (!damCount) return "Add at least one dam.";
	if (combinations < 2) return "Add one more sire or dam to create a multi report.";
	return `${combinations} combinations are ready to generate.`;
}

function Step2({
	handleReportClick,
	setCombinations,
	setPedigreeData,
	pedigreeData,
	credits,
	isSubmitting,
	pricingReady,
}) {
	const [sireInputValue, setSireInputValue] = useState("");
	const [damInputValue, setDamInputValue] = useState("");
	const [selectedSires, setSelectedSires] = useState([]);
	const [selectedDams, setSelectedDams] = useState([]);
	const [isOpenLists, setIsOpenLists] = useState(false);
	const [isLoadingList, setIsLoadingList] = useState(false);
	const [familyModalIsOpen, setFamilyModalIsOpen] = useState(false);
	const [selectedHorseForFamily, setSelectedHorseForFamily] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [isAddHorseModalOpen, setIsAddHorseModalOpen] = useState(false);
	const [alertProps, showAlert] = useSourAlert();
	const sireSearch = useHorseOptions(sireInputValue);
	const damSearch = useHorseOptions(damInputValue);
	const combinations = selectedSires.length * selectedDams.length;
	const canGenerateReport =
		Boolean(String(pedigreeData.name || "").trim()) &&
		Boolean(pedigreeData.ml_model) &&
		combinations >= 2 &&
		pricingReady;
	const guidance = getMultiReportGuidance({
		name: pedigreeData.name,
		sireCount: selectedSires.length,
		damCount: selectedDams.length,
		combinations,
	});

	useEffect(() => {
		setPedigreeData((previous) => ({
			...previous,
			sires: selectedSires.map((horse) => horse.value),
			dams: selectedDams.map((horse) => horse.value),
		}));
		setCombinations(combinations);
	}, [combinations, selectedDams, selectedSires, setCombinations, setPedigreeData]);

	function showDuplicateError() {
		setErrorMessage("That horse is already included in this request.");
		window.setTimeout(() => setErrorMessage(""), 3000);
	}

	function addHorse(option, type) {
		if (!option) return;
		const alreadySelected = [...selectedSires, ...selectedDams].some(
			(item) => item.value === option.value
		);
		if (alreadySelected) {
			showDuplicateError();
			return;
		}
		if (type === "sire") {
			setSelectedSires((previous) => [...previous, option]);
			setSireInputValue("");
		} else {
			setSelectedDams((previous) => [...previous, option]);
			setDamInputValue("");
		}
	}

	function openFamily(horse) {
		setSelectedHorseForFamily(horse);
		setFamilyModalIsOpen(true);
	}

	function onListSelect(listOption) {
		setIsLoadingList(true);
		horseListService
			.listHorses(listOption.value)
			.then((response) => {
				const listHorses = (response.results || []).map((horse) => ({
					value: horse.id,
					label: horse.display_name || horse.name,
				}));
				if (listOption.type === "sire") {
					const damIds = new Set(selectedDams.map((horse) => horse.value));
					setSelectedSires((previous) =>
						Array.from(
							new Map(
								[...previous, ...listHorses.filter((horse) => !damIds.has(horse.value))].map((horse) => [horse.value, horse])
							).values()
						)
					);
				} else {
					const sireIds = new Set(selectedSires.map((horse) => horse.value));
					setSelectedDams((previous) =>
						Array.from(
							new Map(
								[...previous, ...listHorses.filter((horse) => !sireIds.has(horse.value))].map((horse) => [horse.value, horse])
							).values()
						)
					);
				}
			})
			.catch(() => setErrorMessage("The selected list could not be loaded."))
			.finally(() => {
				setIsLoadingList(false);
				setIsOpenLists(false);
			});
	}

	function renderSelectedHorse(horse, type) {
		return (
			<li key={horse.value}>
				<span className="multi-horse-chip__icon"><i className="fa fa-horse-head" aria-hidden="true" /></span>
				<span className="multi-horse-chip__name">{horse.label}</span>
				<button type="button" onClick={() => openFamily(horse)} aria-label={`View ${horse.label} family`} title="View family">
					<i className="fa fa-eye" aria-hidden="true" />
				</button>
				<button
					type="button"
					className="is-remove"
					onClick={() => type === "sire"
						? setSelectedSires((previous) => previous.filter((item) => item.value !== horse.value))
						: setSelectedDams((previous) => previous.filter((item) => item.value !== horse.value))}
					aria-label={`Remove ${horse.label}`}
					title="Remove"
				>
					<i className="fa fa-times" aria-hidden="true" />
				</button>
			</li>
		);
	}

	return (
		<section className="multi-builder-card multi-builder-card--horses" aria-labelledby="multi-horses-title">
			<div className="multi-builder-card__header">
				<div className="multi-builder-card__title">
					<span>2</span>
					<div><h2 id="multi-horses-title">Build combinations</h2><p>Add horses individually or import a saved sire or dam list.</p></div>
				</div>
				<button type="button" className="multi-builder-list-action" onClick={() => setIsOpenLists(true)} disabled={isLoadingList}>
					<i className={`fa ${isLoadingList ? "fa-spinner fa-spin" : "fa-list"}`} aria-hidden="true" />
					{isLoadingList ? "Loading list…" : "Use saved list"}
				</button>
			</div>

			{errorMessage ? <div className="multi-builder-notice multi-builder-notice--error" role="alert">{errorMessage}</div> : null}

			<div className="multi-combination-summary" aria-live="polite">
				<div><span>Sires</span><strong>{selectedSires.length}</strong></div>
				<i className="fa fa-times" aria-hidden="true" />
				<div><span>Dams</span><strong>{selectedDams.length}</strong></div>
				<i className="fa fa-equals" aria-hidden="true" />
				<div className="is-total"><span>Combinations</span><strong>{combinations}</strong></div>
			</div>

			<div className="multi-horse-panels">
				<section className="multi-horse-panel" aria-labelledby="selected-sires-title">
					<div className="multi-horse-panel__header"><div><span>Paternal group</span><h3 id="selected-sires-title">Sires</h3></div><strong>{selectedSires.length}</strong></div>
					<label htmlFor="multi-sire-search">Add a sire</label>
					<Select
						inputId="multi-sire-search"
						inputValue={sireInputValue}
						onInputChange={(value, meta) => { if (meta.action === "input-change") setSireInputValue(value); }}
						options={sireSearch.options}
						onChange={(option) => addHorse(option, "sire")}
						value={null}
						className="react-select-container"
						classNamePrefix="react-select"
						placeholder="Search sire name"
						noOptionsMessage={() => renderNoHorsesFoundMessage(sireSearch.message, () => setIsAddHorseModalOpen(true))}
					/>
					{selectedSires.length ? <ul className="multi-horse-chips">{selectedSires.map((horse) => renderSelectedHorse(horse, "sire"))}</ul> : <p className="multi-horse-panel__empty">No sires selected yet.</p>}
				</section>

				<section className="multi-horse-panel" aria-labelledby="selected-dams-title">
					<div className="multi-horse-panel__header"><div><span>Maternal group</span><h3 id="selected-dams-title">Dams</h3></div><strong>{selectedDams.length}</strong></div>
					<label htmlFor="multi-dam-search">Add a dam</label>
					<Select
						inputId="multi-dam-search"
						inputValue={damInputValue}
						onInputChange={(value, meta) => { if (meta.action === "input-change") setDamInputValue(value); }}
						options={damSearch.options}
						onChange={(option) => addHorse(option, "dam")}
						value={null}
						className="react-select-container"
						classNamePrefix="react-select"
						placeholder="Search dam name"
						noOptionsMessage={() => renderNoHorsesFoundMessage(damSearch.message, () => setIsAddHorseModalOpen(true))}
					/>
					{selectedDams.length ? <ul className="multi-horse-chips">{selectedDams.map((horse) => renderSelectedHorse(horse, "dam"))}</ul> : <p className="multi-horse-panel__empty">No dams selected yet.</p>}
				</section>
			</div>

			<div className="multi-builder-submit">
				<div><strong>{pricingReady ? guidance : "Waiting for credit pricing…"}</strong><span>{canGenerateReport ? `${credits} credits total` : "Selections are saved while you finish the setup."}</span></div>
				<button type="button" onClick={handleReportClick} disabled={!canGenerateReport || isSubmitting}>
					<i className={`fa ${isSubmitting ? "fa-spinner fa-spin" : "fa-layer-group"}`} aria-hidden="true" />
					{isSubmitting ? "Starting analysis…" : combinations ? `Generate ${combinations} combinations` : "Generate multi report"}
				</button>
			</div>

			<FamilyModal isOpenModels={familyModalIsOpen} setIsOpenModels={setFamilyModalIsOpen} dataLot={selectedHorseForFamily} />
			<ListsModal isOpenLists={isOpenLists} setIsOpenLists={setIsOpenLists} onSelect={onListSelect} />
			<AddHorseModal
				isOpen={isAddHorseModalOpen}
				onClose={() => setIsAddHorseModalOpen(false)}
				onResult={(result) => showAlert(result.type === "success" ? addHorseAlerts.success : addHorseAlerts.error(result.message))}
			/>
			<SourAlert {...alertProps} />
		</section>
	);
}

export default Step2;
